import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { API, useAuth } from "@/App";
import axios from "axios";
import PaywallModal from "./PaywallModal";

const PaywallContext = createContext(null);

// Feature lock configuration
const LOCKED_FEATURES = {
  calendar_forecast: { premium: true, label: "Cash Flow Forecast" },
  advanced_insights: { premium: true, label: "Advanced Insights" },
  debt_tracking: { premium: true, label: "Debt Tracking" },
  scenario_planning: { premium: true, label: "Scenario Planning" },
  export_data: { premium: true, label: "Export Data" },
  unlimited_bills: { premium: true, label: "Unlimited Bills" }
};

export function PaywallProvider({ children }) {
  const { user, refreshUser } = useAuth();
  const [isPaywallOpen, setIsPaywallOpen] = useState(false);
  const [paywallTrigger, setPaywallTrigger] = useState("manual");
  const [lockedFeature, setLockedFeature] = useState(null);
  const [trialStatus, setTrialStatus] = useState(null);
  const [sessionCount, setSessionCount] = useState(0);

  // Fetch trial status on mount and when user changes
  useEffect(() => {
    if (user) {
      fetchTrialStatus();
      trackSession();
    }
  }, [user]);

  const fetchTrialStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/trial-status`, {
        withCredentials: true
      });
      setTrialStatus(response.data);
    } catch (error) {
      console.error("[PaywallContext] Error fetching trial status:", error);
    }
  };

  const trackSession = () => {
    const sessions = parseInt(localStorage.getItem('p2p_session_count') || '0', 10);
    const newCount = sessions + 1;
    localStorage.setItem('p2p_session_count', newCount.toString());
    setSessionCount(newCount);
  };

  /**
   * PREMIUM ACCESS PRIORITY:
   * 1. Stripe subscription (active/trialing) = Premium
   * 2. Internal trial (active, not expired) = Premium
   * 3. Everything else = Free
   */
  const isPremium = useCallback(() => {
    if (!trialStatus) {
      return false;
    }
    
    // PRIORITY 1: Stripe subscription (active or trialing)
    // Check subscription_source first (new field)
    if (trialStatus.subscription_source === 'stripe' && 
        ['active', 'trialing'].includes(trialStatus.subscription_status)) {
      return true;
    }
    
    // Fallback: Check stripe_subscription_id directly
    if (trialStatus.stripe_subscription_id && 
        ['active', 'trialing'].includes(trialStatus.subscription_status)) {
      return true;
    }
    
    // PRIORITY 2: Internal trial (active and not expired, no Stripe subscription)
    if (trialStatus.internal_trial_active === true && !trialStatus.stripe_subscription_id) {
      return true;
    }
    
    // Legacy fallback: plan_type trial and not expired
    if (trialStatus.plan_type === 'trial' && 
        !trialStatus.trial_expired && 
        !trialStatus.stripe_subscription_id) {
      return true;
    }
    
    // Final fallback to premium_active or is_premium flag
    if (trialStatus.premium_active === true || trialStatus.is_premium === true) {
      return true;
    }
    
    return false;
  }, [trialStatus]);

  // Check if a specific feature is locked
  const isFeatureLocked = useCallback((featureKey) => {
    if (!LOCKED_FEATURES[featureKey]) return false;
    if (isPremium()) return false;
    return LOCKED_FEATURES[featureKey].premium;
  }, [isPremium]);

  // Show paywall for a specific trigger
  const showPaywall = useCallback((trigger = "manual", feature = null) => {
    // STRICT GUARD: Don't show if trialStatus not yet loaded (prevents race condition)
    if (trialStatus === null) return false;
    
    // Don't show if already premium
    if (isPremium()) return false;
    
    setPaywallTrigger(trigger);
    setLockedFeature(feature);
    setIsPaywallOpen(true);
    
    return true;
  }, [isPremium, trialStatus]);

  // Close paywall
  const closePaywall = useCallback(() => {
    setIsPaywallOpen(false);
    setLockedFeature(null);
  }, []);

  // Check and show paywall for locked feature
  const checkFeatureAccess = useCallback((featureKey, onAccessGranted) => {
    if (!isFeatureLocked(featureKey)) {
      onAccessGranted?.();
      return true;
    }
    
    const featureLabel = LOCKED_FEATURES[featureKey]?.label || featureKey;
    showPaywall("feature_locked", featureLabel);
    return false;
  }, [isFeatureLocked, showPaywall]);

  // Trigger paywall after onboarding
  const triggerPostOnboarding = useCallback(() => {
    if (!isPremium()) {
      setTimeout(() => {
        showPaywall("post_onboarding");
      }, 1500);
    }
  }, [isPremium, showPaywall]);

  // Trigger paywall after first income/bill
  const triggerFirstTransaction = useCallback((type) => {
    if (!isPremium()) {
      const hasSeenPaywall = localStorage.getItem('p2p_first_transaction_paywall');
      if (!hasSeenPaywall) {
        localStorage.setItem('p2p_first_transaction_paywall', 'true');
        setTimeout(() => {
          showPaywall(`first_${type}_added`);
        }, 2000);
      }
    }
  }, [isPremium, showPaywall]);

  // Trigger paywall after multiple sessions
  const triggerSessionBased = useCallback(() => {
    if (!isPremium() && sessionCount >= 3) {
      const lastSessionPaywall = localStorage.getItem('p2p_session_paywall_shown');
      const now = Date.now();
      
      // Show at most once per day
      if (!lastSessionPaywall || now - parseInt(lastSessionPaywall, 10) > 86400000) {
        localStorage.setItem('p2p_session_paywall_shown', now.toString());
        setTimeout(() => {
          showPaywall("returning_user");
        }, 3000);
      }
    }
  }, [isPremium, sessionCount, showPaywall]);

  const value = {
    isPaywallOpen,
    isPremium,
    isFeatureLocked,
    showPaywall,
    closePaywall,
    checkFeatureAccess,
    triggerPostOnboarding,
    triggerFirstTransaction,
    triggerSessionBased,
    trialStatus,
    refreshTrialStatus: fetchTrialStatus,
    sessionCount
  };

  return (
    <PaywallContext.Provider value={value}>
      {children}
      {/* STRICT GUARD: Never render PaywallModal if user is premium or trial status not yet loaded */}
      {!isPremium() && trialStatus !== null && (
        <PaywallModal
          isOpen={isPaywallOpen}
          onClose={closePaywall}
          triggerSource={paywallTrigger}
          lockedFeature={lockedFeature}
        />
      )}
    </PaywallContext.Provider>
  );
}

export function usePaywall() {
  const context = useContext(PaywallContext);
  if (!context) {
    throw new Error("usePaywall must be used within a PaywallProvider");
  }
  return context;
}

// Helper component for locked features
export function LockedFeature({ 
  featureKey, 
  children, 
  fallback = null,
  showOverlay = true 
}) {
  const { isFeatureLocked, showPaywall } = usePaywall();
  const locked = isFeatureLocked(featureKey);

  if (!locked) {
    return children;
  }

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const featureLabel = LOCKED_FEATURES[featureKey]?.label || featureKey;
    showPaywall("feature_locked", featureLabel);
  };

  if (fallback) {
    return fallback;
  }

  if (showOverlay) {
    return (
      <div className="relative cursor-pointer" onClick={handleClick}>
        <div className="opacity-50 pointer-events-none blur-[2px]">
          {children}
        </div>
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-lg backdrop-blur-[1px]">
          <div className="flex items-center gap-2 px-4 py-2 bg-slate-900/90 rounded-full border border-slate-700">
            <span className="text-amber-400 text-sm font-medium">Premium Feature</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div onClick={handleClick} className="cursor-pointer">
      {children}
    </div>
  );
}
